import { useCallback, useEffect, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import { useAsyncCallback } from 'hooks/useAsyncCallback';
import { getMaintenanceEngineDevices, MaintenanceEngineDevice } from 'services/maintenanceEngine';
import { AssignmentRow, getAssignments, getMaintenanceIssues } from 'services/report';
import { MaintenanceIssues } from 'types/report';

const COMPLETED_MAINTENANCE_PREVIEW_SIZE = 5;
const CURRENT_TASKS_PREVIEW_SIZE = 5;
const ASSIGNMENTS_PREVIEW_SIZE = 5;

export const RECENTLY_COMPLETED_WINDOW_DAYS = 7;

const STATUS_PRIORITY: Record<MaintenanceEngineDevice['maintenanceStatus'], number> = {
  OVERDUE: 0,
  DUE: 1,
  UPCOMING: 2,
  NORMAL: 3
};

export const isSensorDevice = (device: MaintenanceEngineDevice) =>
  Boolean(device.iotDeviceName) || device.sourceCounter === 'IOT';

export const isCalendarTriggerDevice = (device: MaintenanceEngineDevice) =>
  device.calculationType?.toLowerCase() === 'date_based';

export type TechnicianWorkload = {
  key: string;
  name: string;
  activeJobs: number;
};

export const useDashboardInjection = () => {
  const [devices, setDevices] = useState<MaintenanceEngineDevice[]>([]);
  const [completedIssues, setCompletedIssues] = useState<MaintenanceIssues[]>([]);
  const [activeIssues, setActiveIssues] = useState<MaintenanceIssues[]>([]);
  const [assignments, setAssignments] = useState<AssignmentRow[]>([]);
  const [selectedClient, setSelectedClient] = useState('');

  const handleGetData = useCallback(async () => {
    const resp = await getMaintenanceEngineDevices({ page: 1, size: 99999 });
    setDevices(resp.devices);
  }, []);

  const handleGetCompletedIssues = useCallback(async () => {
    const resp = await getMaintenanceIssues({
      status: 'RESOLVED',
      page: 1,
      size: 99999,
      orderBy: 'createdAt',
      order: 'desc'
    });
    setCompletedIssues(resp.issues);
  }, []);

  const handleGetActiveIssues = useCallback(async () => {
    const resp = await getMaintenanceIssues({
      status: 'ACTIVE',
      page: 1,
      size: 99999
    });
    setActiveIssues(resp.issues);
  }, []);

  const handleGetAssignments = useCallback(async () => {
    const resp = await getAssignments({
      issueType: 'Maintenance',
      status: 'ACTIVE',
      page: 1,
      size: 99999
    });
    setAssignments(resp.assignments);
  }, []);

  const { asyncCallback, isLoading: loadingDevices } = useAsyncCallback(handleGetData, []);
  const { asyncCallback: asyncFetchCompletedIssues, isLoading: loadingCompleted } =
    useAsyncCallback(handleGetCompletedIssues, []);
  const { asyncCallback: asyncFetchActiveIssues, isLoading: loadingActive } = useAsyncCallback(
    handleGetActiveIssues,
    []
  );
  const { asyncCallback: asyncFetchAssignments, isLoading: loadingAssignments } = useAsyncCallback(
    handleGetAssignments,
    []
  );

  useEffect(() => {
    asyncCallback();
  }, [asyncCallback]);

  useEffect(() => {
    asyncFetchCompletedIssues();
  }, [asyncFetchCompletedIssues]);

  useEffect(() => {
    asyncFetchActiveIssues();
  }, [asyncFetchActiveIssues]);

  useEffect(() => {
    asyncFetchAssignments();
  }, [asyncFetchAssignments]);

  const loading = loadingDevices || loadingCompleted || loadingActive || loadingAssignments;

  const clientOptions = useMemo(() => {
    const names = new Set<string>();
    devices.forEach((device) => device.clientName && names.add(device.clientName));
    completedIssues.forEach((issue) => issue.assetClientName && names.add(issue.assetClientName));
    activeIssues.forEach((issue) => issue.assetClientName && names.add(issue.assetClientName));
    assignments.forEach((row) => row.clientName && names.add(row.clientName));
    return [
      { label: 'All Clients', value: '' },
      ...Array.from(names)
        .sort((a, b) => a.localeCompare(b))
        .map((name) => ({ label: name, value: name }))
    ];
  }, [devices, completedIssues, activeIssues, assignments]);

  const filteredDevices = useMemo(
    () =>
      selectedClient ? devices.filter((device) => device.clientName === selectedClient) : devices,
    [devices, selectedClient]
  );

  const filteredCompletedIssues = useMemo(
    () =>
      selectedClient
        ? completedIssues.filter((issue) => issue.assetClientName === selectedClient)
        : completedIssues,
    [completedIssues, selectedClient]
  );

  const filteredActiveIssues = useMemo(
    () =>
      selectedClient
        ? activeIssues.filter((issue) => issue.assetClientName === selectedClient)
        : activeIssues,
    [activeIssues, selectedClient]
  );

  const filteredAssignments = useMemo(
    () =>
      selectedClient ? assignments.filter((row) => row.clientName === selectedClient) : assignments,
    [assignments, selectedClient]
  );

  const stats = useMemo(() => {
    const dueToday = filteredDevices.filter((device) => device.maintenanceStatus === 'DUE').length;
    const upcoming = filteredDevices.filter(
      (device) => device.maintenanceStatus === 'UPCOMING'
    ).length;
    const overdue = filteredDevices.filter(
      (device) => device.maintenanceStatus === 'OVERDUE'
    ).length;
    const recentlyCompleted = filteredCompletedIssues.filter(
      (issue) =>
        issue.resolutionDate &&
        dayjs().diff(dayjs(issue.resolutionDate), 'day') <= RECENTLY_COMPLETED_WINDOW_DAYS
    ).length;
    const totalMonitored = filteredDevices.length;

    // Offline/Manual only absorbs NORMAL-status manual devices, so every other bucket below
    // is a plain alias of the counts above and always matches them exactly.
    const offlineManual = filteredDevices.filter(
      (device) => device.sourceCounter === 'MANUAL' && device.maintenanceStatus === 'NORMAL'
    ).length;
    const healthy = filteredDevices.filter(
      (device) => device.sourceCounter !== 'MANUAL' && device.maintenanceStatus === 'NORMAL'
    ).length;
    const healthPercent = totalMonitored ? Math.round((healthy / totalMonitored) * 100) : 0;

    return {
      dueToday,
      upcoming,
      overdue,
      recentlyCompleted,
      totalMonitored,
      health: {
        healthy,
        dueSoon: upcoming,
        dueToday,
        overdue,
        offlineManual,
        healthPercent
      }
    };
  }, [filteredDevices, filteredCompletedIssues]);

  const upcomingPreview = useMemo(() => {
    return filteredDevices
      .filter((device) => device.maintenanceStatus === 'UPCOMING')
      .sort((a, b) => (a.remainingHours ?? Infinity) - (b.remainingHours ?? Infinity))
      .slice(0, 5);
  }, [filteredDevices]);

  const comingUpNextPreview = useMemo(() => {
    return filteredDevices
      .filter((device) => device.maintenanceStatus !== 'NORMAL')
      .sort((a, b) => {
        const priorityDiff =
          STATUS_PRIORITY[a.maintenanceStatus] - STATUS_PRIORITY[b.maintenanceStatus];
        if (priorityDiff !== 0) return priorityDiff;
        return (a.remainingHours ?? Infinity) - (b.remainingHours ?? Infinity);
      })
      .slice(0, 5);
  }, [filteredDevices]);

  const sensorPreview = useMemo(() => {
    return filteredDevices
      .filter(isSensorDevice)
      .sort((a, b) => STATUS_PRIORITY[a.maintenanceStatus] - STATUS_PRIORITY[b.maintenanceStatus])
      .slice(0, 5);
  }, [filteredDevices]);

  const calendarPreview = useMemo(() => {
    return filteredDevices
      .filter(isCalendarTriggerDevice)
      .sort((a, b) => STATUS_PRIORITY[a.maintenanceStatus] - STATUS_PRIORITY[b.maintenanceStatus])
      .slice(0, 5);
  }, [filteredDevices]);

  const completedPreview = useMemo(
    () =>
      [...filteredCompletedIssues]
        .sort((a, b) =>
          dayjs(b.resolutionDate ?? b.createdAt).diff(dayjs(a.resolutionDate ?? a.createdAt))
        )
        .slice(0, COMPLETED_MAINTENANCE_PREVIEW_SIZE),
    [filteredCompletedIssues]
  );

  const currentTasksPreview = useMemo(
    () =>
      [...filteredActiveIssues]
        .sort((a, b) => {
          const aDue = a.dueDate ? dayjs(a.dueDate).valueOf() : Infinity;
          const bDue = b.dueDate ? dayjs(b.dueDate).valueOf() : Infinity;
          return aDue - bDue;
        })
        .slice(0, CURRENT_TASKS_PREVIEW_SIZE),
    [filteredActiveIssues]
  );

  const assignmentsPreview = useMemo((): TechnicianWorkload[] => {
    const byTechnician = new Map<string, TechnicianWorkload>();
    filteredAssignments.forEach((row) => {
      if (!row.assignedTo) return;
      const key = row.assignedToUserId != null ? String(row.assignedToUserId) : row.assignedTo;
      const existing = byTechnician.get(key);
      if (existing) {
        existing.activeJobs += 1;
      } else {
        byTechnician.set(key, { key, name: row.assignedTo, activeJobs: 1 });
      }
    });
    return Array.from(byTechnician.values())
      .sort((a, b) => b.activeJobs - a.activeJobs)
      .slice(0, ASSIGNMENTS_PREVIEW_SIZE);
  }, [filteredAssignments]);

  return {
    stats,
    upcomingPreview,
    comingUpNextPreview,
    sensorPreview,
    calendarPreview,
    completedPreview,
    currentTasksPreview,
    assignmentsPreview,
    clientOptions,
    selectedClient,
    setSelectedClient,
    loading
  };
};
